<template>
  <component
    :is="to ? 'router-link' : 'button'"
    v-bind="to ? { to, activeClass: resolvedStateClass ? '' : navActiveClass } : {}"
    class="group flex items-center transition-colors"
    :class="[variantClass, resolvedStateClass || (!to ? stateClass : inactiveClass)]"
    :title="title ?? (!showLabel ? label : undefined)"
    @click.capture="handleClick">
    <!-- Leading slot (e.g. avatar) -->
    <slot name="icon">
      <span class="relative flex-shrink-0">
        <span class="material-symbols-outlined" :class="iconSizeClass">{{ icon }}</span>
        <span
          v-if="badge && badge > 0"
          class="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none"
          :class="badgeColor"
          >{{ badge > 99 ? '99+' : badge }}</span
        >
        <!-- Submenu indicator dot (the bottom bar's version of the chevron) -->
        <span
          v-if="hasSubmenu && (props.stacked || !showLabel)"
          class="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500"></span>
      </span>
    </slot>

    <!-- Label + submenu chevron -->
    <span v-if="showLabel && label" :class="props.stacked ? 'w-full truncate text-center' : 'truncate flex-1'">{{
      label
    }}</span>
    <span
      v-if="hasSubmenu && showLabel && !props.stacked"
      class="material-symbols-outlined text-sm text-gray-400 dark:text-gray-500 flex-shrink-0"
      >chevron_right</span
    >
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

interface Props {
  /** Material symbol name */
  icon?: string;
  /** Text label */
  label?: string;
  /** Router-link destination — turns the button into a <router-link> */
  to?: string;
  /** Manual active state (ignored when `to` is set — router handles it) */
  active?: boolean;
  /** Badge count */
  badge?: number;
  /** Tailwind color class for badge bg  (default: bg-red-500) */
  badgeColor?: string;
  /** Show the label text */
  showLabel?: boolean;
  /**
   * Stacked tab (bottom navigation bar, card t_f6d03638): the icon sits ABOVE
   * its label and the item is a full-height column. Off by default, so every
   * sidebar/drawer caller keeps the row it has always rendered.
   */
  stacked?: boolean;
  /** Tooltip override */
  title?: string;
  /**
   * 'icon' — square 40×40 icon-only button (PersonalSidebar style)
   * 'nav'  — full-width row with icon + optional label (NavigationMenu style)
   */
  variant?: 'icon' | 'nav';
  /** Icon font-size class (default: text-xl for icon, text-2xl for nav) */
  iconSize?: string;
  /** Whether this nav item has a submenu (shows indicator) */
  hasSubmenu?: boolean;
  /**
   * Security-state color override (like room navigation buttons):
   * alarm=red, armed=green, pending=amber, supervise=teal.
   */
  state?: 'alarm' | 'armed' | 'pending' | 'supervise';
}

const props = withDefaults(defineProps<Props>(), {
  badgeColor: 'bg-red-500',
  showLabel: false,
  variant: 'icon',
  stacked: false,
});

const emit = defineEmits<{ (e: 'click', ev: MouseEvent): void; (e: 'toggle-submenu'): void }>();

const route = useRoute();

function handleClick(ev: MouseEvent) {
  if (props.to && props.hasSubmenu) {
    // If already on this section, toggle submenu instead of navigating
    const currentPath = route.path.replace(/^\/h\/[^/]+/, '');
    const targetBase = props.to.replace(/^\/h\/[^/]+/, '');
    if (currentPath.startsWith(targetBase)) {
      ev.preventDefault();
      ev.stopPropagation();
      emit('toggle-submenu');
      return;
    }
  }
  if (!props.to) {
    emit('click', ev);
  }
}

/* ── variant-specific classes ─────────────────────────────────────────── */

const variantClass = computed(() => {
  // Stacked (bottom bar): a column that fills the tab — icon over a 10px label.
  if (props.stacked)
    return 'flex-col justify-center gap-0.5 h-full w-full rounded-lg px-1 py-1 text-[10px] font-medium';
  //   if (props.variant === 'nav') {
  //     return 'gap-3 mx-1 my-1 px-3 py-2 rounded-md text-sm font-medium w-full';
  //   }
  // icon — full-width centered to match nav appearance in collapsed sidebars
  return 'gap-3 m-1 px-3 py-2 rounded-md text-sm font-medium';
});

const iconSizeClass = computed(() => {
  //   if (props.iconSize) return props.iconSize;
  return props.stacked ? 'text-xl' : 'text-2xl'; // consistent icon size across both variants
});

/* ── color states ─────────────────────────────────────────────────────── */

const inactiveClass = 'nav-btn-idle text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white';

const activeIconClass = 'nav-btn-active';
const activeNavClass = 'nav-btn-active';

const stateClass = computed(() => {
  if (!props.active) return inactiveClass;
  return props.variant === 'nav' ? activeNavClass : activeIconClass;
});

// active-class used by router-link — the same ambient wash the settings tree's
// rows wear, so the section you are in reads as tinted glass, never a black chip.
const navActiveClass = computed(() => 'nav-btn-active');

/* ── Security state colors (mirror room navigation) ─────────────────────── */

const SECURITY_STATE_CLASSES: Record<NonNullable<Props['state']>, { idle: string; active: string }> = {
  alarm: {
    idle: 'text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-800 dark:hover:text-red-200 ring-1 ring-red-100/80 dark:ring-red-900/60',
    active: 'bg-red-50 dark:bg-red-900/35 text-red-700 dark:text-red-200 ring-1 ring-red-200/80 dark:ring-red-800/80',
  },
  armed: {
    idle: 'text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 hover:text-green-800 dark:hover:text-green-200 ring-1 ring-green-100/80 dark:ring-green-900/60',
    active:
      'bg-green-50 dark:bg-green-900/35 text-green-700 dark:text-green-200 ring-1 ring-green-200/80 dark:ring-green-800/80',
  },
  pending: {
    idle: 'text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 hover:text-amber-800 dark:hover:text-amber-200 ring-1 ring-amber-100/80 dark:ring-amber-900/60',
    active:
      'bg-amber-50 dark:bg-amber-900/35 text-amber-700 dark:text-amber-200 ring-1 ring-amber-200/80 dark:ring-amber-800/80',
  },
  supervise: {
    idle: 'text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:text-teal-800 dark:hover:text-teal-200 ring-1 ring-teal-100/80 dark:ring-teal-900/60',
    active:
      'bg-teal-50 dark:bg-teal-900/35 text-teal-700 dark:text-teal-200 ring-1 ring-teal-200/80 dark:ring-teal-800/80',
  },
};

/** Whether the current route matches this item's destination. */
const isNavActive = computed(() => {
  if (!props.to) return false;
  const currentPath = route.path.replace(/^\/h\/[^/]+/, '');
  const targetBase = props.to.replace(/^\/h\/[^/]+/, '');
  return currentPath.startsWith(targetBase);
});

/** State classes override the default active/inactive styling entirely. */
const resolvedStateClass = computed(() => {
  if (!props.state) return '';
  const entry = SECURITY_STATE_CLASSES[props.state];
  return isNavActive.value ? entry.active : entry.idle;
});
</script>

<style scoped>
/* One nav row: the hover is a neutral wash and the selected item is an
   ambient-tint wash — the same language as the settings tree and the rooms
   rail. The edge is drawn with an inset ring so selecting never shifts layout. */
.nav-btn-idle:hover {
  background: rgba(15, 23, 42, 0.05);
}

.dark .nav-btn-idle:hover {
  background: rgba(255, 255, 255, 0.07);
}

.nav-btn-active,
.nav-btn-active:hover {
  background: rgb(var(--ui-tint) / 0.2);
  color: #0f172a;
  box-shadow:
    inset 0 0 0 1px rgb(var(--ui-tint) / 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.35),
    0 8px 20px -16px rgb(var(--ui-tint) / 0.9);
}

.dark .nav-btn-active,
.dark .nav-btn-active:hover {
  background: rgb(var(--ui-tint) / 0.28);
  color: #fff;
  box-shadow:
    inset 0 0 0 1px rgb(var(--ui-tint) / 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 10px 24px -18px rgb(var(--ui-tint));
}

/* Normal mode carries no ambient colour: a plain neutral highlight. */
html:not([data-card-style='glass']) .nav-btn-active,
html:not([data-card-style='glass']) .nav-btn-active:hover {
  background: rgba(15, 23, 42, 0.07);
  color: #0f172a;
  box-shadow: none;
}

html:not([data-card-style='glass']).dark .nav-btn-active,
html:not([data-card-style='glass']).dark .nav-btn-active:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
</style>
